// src/components/ProductManagement/ProductEditModal.jsx
import React, { useState, useEffect } from 'react';

const ProductEditModal = ({ isOpen, onClose, onSave, isSaving, categories, product }) => {
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        categoryId: product.categoryId || product.category?.id || '',
        description: product.description || '',
        isActive: product.isActive !== undefined ? product.isActive : true
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Product name is required');
      return;
    }
    
    if (!formData.categoryId) {
      alert('Category is required');
      return;
    }

    const payload = {
      name: formData.name,
      categoryId: parseInt(formData.categoryId),
      description: formData.description,
      isActive: formData.isActive
    };

    onSave(product.id, payload);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      categoryId: '',
      description: '',
      isActive: true
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={handleClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Edit Product</h3>
          <button 
            style={styles.closeBtn} 
            onClick={handleClose}
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.modalForm}>
          <div style={styles.formGroup}>
            <label htmlFor="name" style={styles.label}>Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter product name"
              required
              disabled={isSaving}
              style={{...styles.input, ...(isSaving && styles.inputDisabled)}}
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="categoryId" style={styles.label}>Category *</label>
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              required
              disabled={isSaving}
              style={{...styles.input, ...(isSaving && styles.inputDisabled)}}
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="description" style={styles.label}>Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter product description"
              rows="4"
              disabled={isSaving}
              style={{...styles.textarea, ...(isSaving && styles.inputDisabled)}}
            />
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={isSaving}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Active</span>
            </label>
          </div>

          <div style={styles.modalActions}>
            <button
              type="button"
              style={{...styles.btn, ...styles.btnSecondary}}
              onClick={handleClose}
              disabled={isSaving}
              onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#4B5563')}
              onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = '#6B7280')}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.btn, 
                ...styles.btnPrimary,
                ...(isSaving && styles.btnDisabled)
              }}
              disabled={isSaving}
              onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#1a3070')}
              onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = '#1E3A8A')}
            >
              {isSaving ? 'Updating...' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
    margin: '2%'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1E3A8A',
    color: '#F9FAFB',
    padding: '20px 25px',
    fontWeight: 700,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.2em'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#F9FAFB',
    fontSize: '1.8em',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    transition: 'color 0.15s'
  },
  modalForm: {
    padding: '25px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontWeight: 600,
    color: '#555',
    marginBottom: '8px',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    fontSize: '14px',
    fontFamily: 'inherit'
  },
  inputDisabled: {
    backgroundColor: '#F3F4F6',
    color: '#9CA3AF',
    cursor: 'not-allowed'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px'
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: 0
  },
  checkbox: {
    width: 'auto',
    marginRight: '8px',
    cursor: 'pointer'
  },
  checkboxText: {
    fontWeight: 600,
    color: '#555'
  },
  modalActions: {
    borderTop: '1px solid #F3F4F6',
    padding: '15px 25px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    margin: '0 -25px -25px -25px'
  },
  btn: {
    padding: '10px 20px',
    borderRadius: '6px',
    fontWeight: 600,
    transition: 'background-color 0.2s, box-shadow 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '14px'
  },
  btnPrimary: {
    backgroundColor: '#1E3A8A',
    color: '#F9FAFB'
  },
  btnSecondary: {
    backgroundColor: '#6B7280',
    color: 'white'
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
};

export default ProductEditModal;